import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, API } from "@/App";
import { Loader2, User, Lock, Eye, EyeOff, AlertCircle, Smartphone, Mail, ArrowLeft, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_pressnet/artifacts/nmfi6tck_Choluteca_net3-BLANCO.png";

// Helper function to make requests with retry logic for mobile connections
const axiosWithRetry = async (config, maxRetries = 2) => {
  let lastError = null;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Longer timeout for mobile connections (30 seconds)
      const response = await axios({
        ...config,
        timeout: 30000,
      });
      return response;
    } catch (error) {
      lastError = error;
      // Only retry on network errors, not on 4xx/5xx responses
      if (error.response) {
        throw error; // Server responded, don't retry
      }
      // Wait before retry (exponential backoff)
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
      }
    }
  }
  throw lastError;
};

export default function AdminLogin() {
  const { user, loading, setUser, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  
  // Mobile code login states
  const [loginMode, setLoginMode] = useState("password"); // "password" | "code-request" | "code-verify"
  const [code, setCode] = useState("");
  const [emailHint, setEmailHint] = useState("");
  const [codeExpiry, setCodeExpiry] = useState(null);

  useEffect(() => {
    if (!loading && user) {
      navigate("/admin", { replace: true });
    }
  }, [user, loading, navigate]);

  // Countdown timer for code expiry
  useEffect(() => {
    if (codeExpiry) {
      const timer = setInterval(() => {
        const remaining = Math.max(0, codeExpiry - Date.now());
        if (remaining === 0) {
          setCodeExpiry(null);
          setLoginMode("code-request");
          setError("El código ha expirado. Solicita uno nuevo.");
        }
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [codeExpiry]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    
    const trimmedUsername = username.trim();
    
    if (!trimmedUsername || !password) {
      setError("Por favor ingresa usuario y contraseña");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axiosWithRetry({
        method: 'POST',
        url: `${API}/auth/login`,
        data: { username: trimmedUsername.toLowerCase(), password },
        // Don't use withCredentials - we'll use localStorage token instead
        // This avoids Safari/iOS cookie blocking issues
        withCredentials: false,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // Store token in localStorage - this is the PRIMARY auth method
      if (response.data.session_token) {
        localStorage.setItem("session_token", response.data.session_token);
      }
      
      // Remove token from response before setting user
      const userData = { ...response.data };
      delete userData.session_token;
      
      setUser(userData);
      toast.success(`¡Bienvenido, ${userData.name || userData.username}!`);
      
      // Small delay to ensure state is updated
      setTimeout(() => {
        navigate("/admin", { replace: true });
      }, 100);
      
    } catch (error) {
      console.error("Login error:", error);
      
      let message = "Error al iniciar sesión";
      let showRetryHint = false;
      
      if (error.response) {
        // Server responded with error
        message = error.response.data?.detail || message;
        
        if (error.response.status === 401) {
          message = "Usuario o contraseña incorrectos";
        } else if (error.response.status === 403) {
          message = "Tu cuenta está desactivada. Contacta al administrador.";
        } else if (error.response.status === 400) {
          message = "Datos de acceso inválidos";
        }
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        // Timeout error
        message = "La conexión tardó demasiado. Verifica tu señal e intenta de nuevo.";
        showRetryHint = true;
      } else if (error.request) {
        // Request made but no response - after retries
        message = "No se puede conectar con el servidor. Verifica tu conexión a internet.";
        showRetryHint = true;
      }
      
      setError(message);
      toast.error(showRetryHint ? `${message} Usa el botón de reintentar.` : message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError("");
    
    const trimmedUsername = username.trim();
    
    if (!trimmedUsername) {
      setError("Por favor ingresa tu nombre de usuario");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axiosWithRetry({
        method: 'POST',
        url: `${API}/auth/request-code`,
        data: { username: trimmedUsername.toLowerCase() },
        headers: { 'Content-Type': 'application/json' }
      });
      
      setEmailHint(response.data.email_hint || "");
      setCodeExpiry(Date.now() + (response.data.expires_in || 600) * 1000);
      setLoginMode("code-verify");
      toast.success("Código enviado a tu correo");
      
    } catch (error) {
      console.error("Request code error:", error);
      
      let message = "Error al solicitar código";
      if (error.response?.data?.detail) {
        message = error.response.data.detail;
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        message = "La conexión tardó demasiado. Intenta de nuevo.";
      } else if (error.request) {
        message = "No se puede conectar con el servidor. Verifica tu conexión.";
      }
      
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError("");
    
    const trimmedCode = code.trim();
    
    if (!trimmedCode || trimmedCode.length !== 6) {
      setError("Ingresa el código de 6 dígitos");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axiosWithRetry({
        method: 'POST',
        url: `${API}/auth/verify-code`,
        data: { username: username.trim().toLowerCase(), code: trimmedCode },
        // Don't use withCredentials - we'll use localStorage token instead
        withCredentials: false,
        headers: { 'Content-Type': 'application/json' }
      });
      
      // Store token in localStorage - this is the PRIMARY auth method
      if (response.data.session_token) {
        localStorage.setItem("session_token", response.data.session_token);
      }
      
      const userData = { ...response.data };
      delete userData.session_token;
      
      setUser(userData);
      toast.success(`¡Bienvenido, ${userData.name || userData.username}!`);
      
      setTimeout(() => {
        navigate("/admin", { replace: true });
      }, 100);
      
    } catch (error) {
      console.error("Verify code error:", error);
      
      let message = "Código inválido";
      if (error.response?.data?.detail) {
        message = error.response.data.detail;
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        message = "La conexión tardó demasiado. Intenta de nuevo.";
      } else if (error.request) {
        message = "No se puede conectar con el servidor. Verifica tu conexión.";
      }
      
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTimeRemaining = () => {
    if (!codeExpiry) return "";
    const remaining = Math.max(0, Math.floor((codeExpiry - Date.now()) / 1000));
    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const resetToPassword = () => {
    setLoginMode("password");
    setCode("");
    setEmailHint("");
    setCodeExpiry(null);
    setError("");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-bg">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-2xl p-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="bg-slate-900 rounded-2xl p-4">
              <img src={LOGO_URL} alt="Choluteca.net" className="h-10" />
            </div>
          </div>

          <div className="text-center mb-8">
            <h1 className="font-heading text-2xl font-bold text-brand-text mb-2">
              Panel de Administración
            </h1>
            <p className="text-brand-textSecondary">
              {loginMode === "password" && "Ingresa tus credenciales para acceder"}
              {loginMode === "code-request" && "Ingresa tu usuario para recibir un código"}
              {loginMode === "code-verify" && "Ingresa el código enviado a tu correo"}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            </div>
          )}

          {/* Password Login Form */}
          {loginMode === "password" && (
            <form onSubmit={handleLogin} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-sm font-medium text-gray-700">
                  Usuario
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="username"
                    type="text"
                    placeholder="Tu nombre de usuario"
                    value={username}
                    onChange={(e) => {
                      setUsername(e.target.value);
                      setError("");
                    }}
                    className="pl-10 h-12"
                    data-testid="login-username-input"
                    autoComplete="username"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                  Contraseña
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Tu contraseña"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError("");
                    }}
                    className="pl-10 pr-10 h-12"
                    data-testid="login-password-input"
                    autoComplete="current-password"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    disabled={isLoading}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-semibold rounded-xl"
                disabled={isLoading}
                data-testid="login-submit-button"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Iniciando sesión...
                  </>
                ) : (
                  "Iniciar Sesión"
                )}
              </Button>

              {/* Mobile Code Login Option */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">o</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full h-12 border-2 border-dashed border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-xl"
                onClick={() => setLoginMode("code-request")}
                disabled={isLoading}
                data-testid="mobile-login-button"
              >
                <Smartphone className="mr-2 h-5 w-5" />
                Acceso Móvil (Código por Email)
              </Button>
            </form>
          )}

          {/* Request Code Form */}
          {loginMode === "code-request" && (
            <form onSubmit={handleRequestCode} className="space-y-5">
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                <p className="text-sm text-emerald-800">
                  <Smartphone className="inline h-4 w-4 mr-1" />
                  Recibirás un código de 6 dígitos en tu correo electrónico registrado.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="username-code" className="text-sm font-medium text-gray-700">
                  Usuario
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="username-code"
                    type="text"
                    placeholder="Tu nombre de usuario"
                    value={username}
                    onChange={(e) => {
                      setUsername(e.target.value);
                      setError("");
                    }}
                    className="pl-10 h-12"
                    data-testid="code-username-input"
                    autoComplete="username"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl"
                disabled={isLoading}
                data-testid="request-code-button"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Enviando código...
                  </>
                ) : (
                  <>
                    <Mail className="mr-2 h-5 w-5" />
                    Enviar Código
                  </>
                )}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full text-gray-500"
                onClick={resetToPassword}
                disabled={isLoading}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Volver al login normal
              </Button>
            </form>
          )}

          {/* Verify Code Form */}
          {loginMode === "code-verify" && (
            <form onSubmit={handleVerifyCode} className="space-y-5">
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-center">
                <Mail className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
                <p className="text-sm text-emerald-800">
                  Código enviado a <strong>{emailHint}</strong>
                </p>
                {codeExpiry && (
                  <p className="text-xs text-emerald-600 mt-1">
                    Expira en: <strong className="font-mono">{formatTimeRemaining()}</strong>
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="code" className="text-sm font-medium text-gray-700">
                  Código de 6 dígitos
                </Label>
                <Input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  value={code}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setCode(value);
                    setError("");
                  }}
                  className="h-14 text-center text-2xl font-mono tracking-[0.5em] font-bold"
                  data-testid="verify-code-input"
                  autoComplete="one-time-code"
                  disabled={isLoading}
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl"
                disabled={isLoading || code.length !== 6}
                data-testid="verify-code-button"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Verificando...
                  </>
                ) : (
                  "Verificar y Acceder"
                )}
              </Button>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 text-gray-500"
                  onClick={() => {
                    setCode("");
                    setLoginMode("code-request");
                  }}
                  disabled={isLoading}
                >
                  Reenviar código
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="flex-1 text-gray-500"
                  onClick={resetToPassword}
                  disabled={isLoading}
                >
                  <ArrowLeft className="mr-1 h-4 w-4" />
                  Volver
                </Button>
              </div>
            </form>
          )}

          <p className="text-center text-sm text-gray-500 mt-6">
            Acceso restringido a administradores
          </p>
          
          <p className="text-center text-xs text-gray-400 mt-2">
            v2.1 - Sesión válida por 30 días
          </p>
        </div>
      </div>
    </div>
  );
}
